import type {
  FollowUpEventType,
  FollowUpManualTracking,
  FollowUpQueue,
  FollowUpSource,
  FollowUpStatus,
} from '@/lib/api-client'

/**
 * Display metadata for each follow-up queue — label, one-line description and a
 * color token pair (dot + badge classes) reused across tiles / table tags / history.
 */
export interface QueueMeta {
  label: string
  description: string
  dotClassName: string
  badgeClassName: string
}

export const QUEUE_META: Record<FollowUpQueue, QueueMeta> = {
  package_done: {
    label: 'Package done',
    description: 'All packages used up, visited in last 60 days, no new booking',
    dotClassName: 'bg-info',
    badgeClassName: 'bg-info/10 text-info',
  },
  last_session: {
    label: '1 session left',
    description: 'Package has 1 session left, visited in last 6 months, not booked',
    dotClassName: 'bg-warning',
    badgeClassName: 'bg-warning/10 text-warning',
  },
  stale_package: {
    label: 'Stalled package',
    description: '2+ sessions left (RM 200+), no visit for 60 days to 6 months',
    dotClassName: 'bg-destructive',
    badgeClassName: 'bg-destructive/10 text-destructive',
  },
  consult_no_purchase: {
    label: 'Consult, no purchase',
    description: 'Consult only in last 30 days, no treatment bought since',
    dotClassName: 'bg-chart-4',
    badgeClassName: 'bg-chart-4/10 text-chart-4',
  },
  no_show: {
    label: 'No-show / cancelled',
    description: 'Missed or cancelled in last 7 days, not rebooked',
    dotClassName: 'bg-text-muted',
    badgeClassName: 'bg-muted text-text-muted',
  },
  lapsed_regular: {
    label: 'Lapsed regular',
    description: '3+ visits, last visit 3 to 6 months ago, no open package',
    dotClassName: 'bg-text-dark',
    badgeClassName: 'bg-muted text-text-dark',
  },
}

/**
 * CA work order (most time-sensitive first). This is the order tiles are shown
 * in and the API's default `priority` sort. It is NOT the queue-builder's
 * dedup order (that one picks which single queue a patient lands in).
 */
export const QUEUE_ORDER: FollowUpQueue[] = [
  'no_show',
  'consult_no_purchase',
  'last_session',
  'package_done',
  'stale_package',
  'lapsed_regular',
]

/** Days after `weekStart` at which the nightly reconciler expires an unresolved row. */
export const FOLLOW_UP_EXPIRY_DAYS = 28

export interface StatusMeta {
  label: string
  dotClassName: string
}

export const STATUS_META: Record<FollowUpStatus, StatusMeta> = {
  pending: { label: 'Not contacted', dotClassName: 'bg-text-light' },
  contacted: { label: 'WA sent', dotClassName: 'bg-info' },
  replied: { label: 'Replied', dotClassName: 'bg-warning' },
  booked: { label: 'Booked', dotClassName: 'bg-success' },
  converted: { label: 'Repurchased', dotClassName: 'bg-success' },
  no_answer: { label: 'No answer', dotClassName: 'bg-destructive' },
  callback: { label: 'Call back requested', dotClassName: 'bg-warning' },
  thinking: { label: 'Thinking it over', dotClassName: 'bg-warning' },
  not_interested: { label: 'Not interested', dotClassName: 'bg-destructive' },
  expired: { label: 'Expired', dotClassName: 'bg-text-muted' },
  blacklisted: { label: 'Blacklisted', dotClassName: 'bg-destructive' },
}

/** Segmented "This week" tab filter groups → underlying status filter value. */
export const STATUS_SEGMENTS = [
  { key: 'open', label: 'Open' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'booked', label: 'Booked' },
  { key: 'all', label: 'All' },
] as const

export const EVENT_LABELS: Record<FollowUpEventType, string> = {
  queued: 'Entered queue',
  assigned: 'Assigned',
  wa_sent: 'Sent via WhatsApp (CA phone)',
  wa_opened: 'Opened in WhatsApp',
  called: 'Called',
  replied: 'Replied (marked by CA)',
  booked: 'Booked',
  repurchased: 'Repurchased',
  no_answer: 'No answer',
  callback: 'Call back requested',
  thinking: 'Thinking it over',
  not_interested: 'Not interested',
  note: 'Note',
  expired: 'Expired',
  blacklisted: 'Blacklisted',
}

/** Short labels for the History tab (action column + "What staff recorded" bars). */
export const ACTION_LABELS: Record<FollowUpEventType, string> = {
  ...EVENT_LABELS,
  wa_sent: 'Sent via WhatsApp',
  replied: 'Replied',
}

/** Manual event types that count as "contacted by staff" in History & report
 * (mirrors MANUAL_CONTACT_TYPES in the API). */
export const STAFF_ACTION_TYPES: FollowUpEventType[] = [
  'wa_opened',
  'wa_sent',
  'called',
  'replied',
  'no_answer',
  'callback',
  'thinking',
  'not_interested',
  'blacklisted',
]

export const STAFF_ROLE_LABELS: Record<string, string> = {
  clinic_staff: 'CA',
  doctor: 'Doctor',
  marketing: 'Marketing',
  branch_manager: 'Manager',
  admin: 'Admin',
  super_admin: 'Admin',
  analyst: 'Analyst',
}

/**
 * The `wa_sent` / `replied` labels above assume a MANUAL source (CA's own
 * phone). An AUTO `wa_sent` / `replied` event (source `auto_wa`, from the
 * Nurin inbox) means the message actually went out through Nurin's
 * WhatsApp Cloud API connection — use a different label for those so the
 * timeline doesn't imply the wa.me redirect flow was used.
 */
export function eventLabel(type: FollowUpEventType, source: FollowUpSource): string {
  if (source === 'auto_wa') {
    if (type === 'wa_sent') return 'Sent from Nurin inbox'
    if (type === 'replied') return 'Replied'
  }
  return EVENT_LABELS[type]
}

export const MANUAL_OUTCOME_OPTIONS = [
  { type: 'no_answer' as const, label: 'No answer' },
  { type: 'callback' as const, label: 'Call back' },
  { type: 'thinking' as const, label: 'Thinking it over' },
  { type: 'not_interested' as const, label: 'Not interested' },
]

/** "After contacting, mark as" buttons for the wa.me redirect drawer flow. */
export const MANUAL_TRACKING_OPTIONS: { type: FollowUpManualTracking; label: string }[] = [
  { type: 'wa_sent', label: 'Sent via WhatsApp' },
  { type: 'called', label: 'Called' },
  { type: 'replied', label: 'Replied' },
]

export function isAutoSource(source: FollowUpSource): boolean {
  return source === 'auto_wa' || source === 'auto_avixo'
}

export function sourceLabel(source: FollowUpSource): string {
  switch (source) {
    case 'auto_wa':
      return 'AUTO · WA INBOX'
    case 'auto_avixo':
      return 'AUTO · AVIXO'
    case 'system':
      return 'AUTO'
    case 'manual':
      return 'MANUAL'
  }
}
